<div class="container mx-auto px-4 py-6">
  <!-- Header -->
  <div class="mb-8">
    <h1 class="text-2xl font-bold text-gray-900">Retiro de Acuerdo Corporativo</h1>
    <p class="text-gray-600 mt-1">Castigar cartera de un acuerdo corporativo (empresa y participantes)</p>
  </div>

  <!-- Stepper -->
  <div class="mb-8">
    <div class="flex items-center justify-between">
      <div class="flex items-center" *ngFor="let step of [1, 2, 3, 4]; let i = index">
        <div class="flex items-center">
          <div 
            [ngClass]="{
              'bg-green-600 text-white': currentStep === 4 && operationSuccess && step === 4,
              'bg-red-600 text-white': currentStep === 4 && !operationSuccess && step === 4,
              'bg-blue-600 text-white': currentStep >= step && !(currentStep === 4 && step === 4),
              'bg-gray-200 text-gray-600': currentStep < step
            }"
            class="w-10 h-10 rounded-full flex items-center justify-center font-semibold">
            <span *ngIf="!(currentStep === 4 && step === 4)">{{ step }}</span>
            <svg *ngIf="currentStep === 4 && step === 4 && operationSuccess" class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
            </svg>
            <svg *ngIf="currentStep === 4 && step === 4 && !operationSuccess" class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </div>
          <span class="ml-2 text-sm font-medium hidden md:inline" 
                [ngClass]="{'text-blue-600': currentStep >= step, 'text-gray-500': currentStep < step}">
            {{ step === 1 ? 'Seleccionar' : step === 2 ? 'Cuotas' : step === 3 ? 'Confirmar' : 'Resultado' }}
          </span>
        </div>
        <div *ngIf="i < 3" class="w-12 md:w-20 h-1 mx-2 md:mx-4" 
             [ngClass]="{'bg-blue-600': currentStep > step, 'bg-gray-200': currentStep <= step}"></div>
      </div>
    </div>
  </div>

  <!-- Paso 1: Buscar y seleccionar acuerdo corporativo -->
  <div *ngIf="currentStep === 1" class="bg-white rounded-lg shadow-md p-6">
    <h2 class="text-lg font-semibold mb-4">Buscar Empresa</h2>
    
    <form [formGroup]="searchForm" (ngSubmit)="searchCompany()" class="mb-6">
      <div class="flex gap-4">
        <input 
          type="text" 
          formControlName="searchTerm"
          placeholder="Buscar por nombre o NIT..."
          class="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent">
        <button 
          type="submit" 
          [disabled]="isSearching || searchForm.invalid"
          class="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed">
          <span *ngIf="isSearching">Buscando...</span>
          <span *ngIf="!isSearching">Buscar</span>
        </button>
      </div>
    </form>

    <!-- Lista de empresas -->
    <div *ngIf="searchPerformed && companies.length > 0" class="mb-6">
      <h3 class="text-sm font-medium text-gray-700 mb-2">Empresas encontradas:</h3>
      <div class="space-y-2 max-h-60 overflow-y-auto">
        <div 
          *ngFor="let company of companies" 
          (click)="selectCompany(company)"
          [ngClass]="{'ring-2 ring-blue-500 bg-blue-50': selectedCompanyId === company.id}"
          class="p-3 border rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
          <div class="font-medium">{{ company.name }}</div>
          <div class="text-sm text-gray-500">NIT: {{ company.documentId }}</div>
        </div>
      </div>
    </div>

    <div *ngIf="searchPerformed && companies.length === 0 && !isSearching" class="text-center py-8 text-gray-500">
      No se encontraron empresas con ese criterio de búsqueda
    </div>

    <!-- Lista de acuerdos corporativos activos -->
    <div *ngIf="corporateEnrollments.length > 0" class="mt-6">
      <h3 class="text-sm font-medium text-gray-700 mb-2">Acuerdos corporativos activos:</h3>
      <div class="space-y-3">
        <div 
          *ngFor="let e of corporateEnrollments"
          (click)="selectEnrollment(e)"
          [ngClass]="{'opacity-50 cursor-not-allowed': !e.canBeWithdrawn}"
          class="p-4 border rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
          <div class="flex justify-between items-start">
            <div>
              <div class="font-medium">{{ e.projectMnemonic }} - {{ e.projectName }}</div>
              <div class="text-sm text-gray-500">{{ e.companyName }}</div>
            </div>
            <span class="px-2 py-1 text-xs rounded-full" 
                  [ngClass]="{'bg-green-100 text-green-800': e.canBeWithdrawn, 'bg-gray-100 text-gray-600': !e.canBeWithdrawn}">
              {{ e.pendingCompanyInstallments + e.pendingParticipantInstallments }} cuotas pendientes
            </span>
          </div>
          <div class="mt-2 text-sm">
            <span class="text-gray-600">Total: </span>
            <span class="font-medium text-blue-600">{{ formatCurrency(e.totalPendingAmount, e.currencySymbol) }}</span>
          </div>
        </div>
      </div>
    </div>

    <div *ngIf="isLoading" class="text-center py-8">
      <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
    </div>
  </div>

    <!-- Paso 2: Revisar Cuotas -->
    <div *ngIf="currentStep === 2 && selectedEnrollment" class="bg-white rounded-lg shadow-md p-6">
      <div class="mb-6">
        <h2 class="text-lg font-semibold">Revisar Plan de Pagos</h2>
        <p class="text-gray-600">{{ selectedEnrollment.companyName }} - {{ selectedEnrollment.projectName }}</p>
      </div>

      <div class="flex justify-between items-center mb-4">
        <span class="text-sm text-gray-600">Seleccione las cuotas a castigar:</span>
        <div class="space-x-2">
          <button (click)="selectAllInstallments()" class="text-sm text-blue-600 hover:text-blue-800">Seleccionar todas</button>
          <button (click)="deselectAllInstallments()" class="text-sm text-gray-600 hover:text-gray-800">Deseleccionar</button>
        </div>
      </div>

      <!-- Resumen del acuerdo -->
      <div class="bg-gray-50 rounded-lg p-4 mb-6">
        <div class="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <p class="text-sm text-gray-500">Empresa</p>
            <p class="font-medium">{{ selectedEnrollment.companyName }}</p>
          </div>
          <div>
            <p class="text-sm text-gray-500">Proyecto</p>
            <p class="font-medium">{{ selectedEnrollment.projectName }}</p>
          </div>
          <div>
            <p class="text-sm text-gray-500">Participantes</p>
            <p class="font-medium">{{ selectedEnrollment.totalParticipants }}</p>
          </div>
          <div>
            <p class="text-sm text-gray-500">Moneda</p>
            <p class="font-medium">{{ selectedEnrollment.currencyCode || 'COP' }}</p>
          </div>
        </div>
      </div>

      <!-- Cuotas de la empresa -->
      <div *ngIf="selectedEnrollment.companyPendingPlans.length > 0" class="mb-6">
        <h3 class="text-md font-medium text-gray-900 mb-3 flex items-center">
          <span class="w-3 h-3 bg-blue-500 rounded-full mr-2"></span>
          Cuotas de la Empresa ({{ availableCompanyInstallmentsCount }})
        </h3>
        <div class="overflow-x-auto">
          <table class="min-w-full divide-y divide-gray-200">
            <thead class="bg-gray-50">
              <tr>
                <th class="w-12 px-4 py-3"></th>
                <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cuota</th>
                <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Vencimiento</th>
                <th class="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Monto</th>
                <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estado</th>
              </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-200">
              <tr *ngFor="let plan of selectedEnrollment.companyPendingPlans" 
                  [class.opacity-50]="plan.isPunished"
                  class="hover:bg-gray-50">
                <td class="px-4 py-3">
                  <input type="checkbox" 
                         [checked]="isInstallmentSelected(plan.corporatePaymentPlanId)"
                         (change)="toggleInstallment(plan.corporatePaymentPlanId)"
                         [disabled]="plan.isPunished"
                         class="rounded border-gray-300 text-blue-600 focus:ring-blue-500">
                </td>
                <td class="px-4 py-3 text-sm text-gray-900">{{ plan.installmentNumber }}</td>
                <td class="px-4 py-3 text-sm text-gray-500">{{ formatDate(plan.dueDate) }}</td>
                <td class="px-4 py-3 text-sm text-gray-900 text-right font-medium">
                  {{ formatCurrency(plan.pendingAmount, selectedEnrollment.currencySymbol) }}
                </td>
                <td class="px-4 py-3">
                  <span class="px-2 py-1 text-xs rounded-full"
                        [ngClass]="{
                          'bg-yellow-100 text-yellow-800': plan.paymentStatusDescription === 'Pendiente',
                          'bg-red-100 text-red-800': plan.isOverdue,
                          'bg-gray-100 text-gray-600': plan.isPunished
                        }">
                    {{ plan.isPunished ? 'Castigada' : (plan.isOverdue ? 'Vencida (' + plan.daysOverdue + ' días)' : plan.paymentStatusDescription) }}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Cuotas de participantes -->
      <div *ngIf="selectedEnrollment.participantPendingPlans.length > 0">
        <h3 class="text-md font-medium text-gray-900 mb-3 flex items-center">
          <span class="w-3 h-3 bg-green-500 rounded-full mr-2"></span>
          Cuotas de Participantes ({{ availableParticipantInstallmentsCount }})
        </h3>
        <div class="overflow-x-auto">
          <table class="min-w-full divide-y divide-gray-200">
            <thead class="bg-gray-50">
              <tr>
                <th class="w-12 px-4 py-3"></th>
                <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Participante</th>
                <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cuota</th>
                <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Vencimiento</th>
                <th class="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Monto</th>
                <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estado</th>
              </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-200">
              <tr *ngFor="let plan of selectedEnrollment.participantPendingPlans" 
                  [class.opacity-50]="plan.isPunished"
                  class="hover:bg-gray-50">
                <td class="px-4 py-3">
                  <input type="checkbox" 
                         [checked]="isInstallmentSelected(plan.corporatePaymentPlanId)"
                         (change)="toggleInstallment(plan.corporatePaymentPlanId)"
                         [disabled]="plan.isPunished"
                         class="rounded border-gray-300 text-blue-600 focus:ring-blue-500">
                </td>
                <td class="px-4 py-3 text-sm text-gray-900">{{ plan.participantName }}</td>
                <td class="px-4 py-3 text-sm text-gray-900">{{ plan.installmentNumber }}</td>
                <td class="px-4 py-3 text-sm text-gray-500">{{ formatDate(plan.dueDate) }}</td>
                <td class="px-4 py-3 text-sm text-gray-900 text-right font-medium">
                  {{ formatCurrency(plan.pendingAmount, selectedEnrollment.currencySymbol) }}
                </td>
                <td class="px-4 py-3">
                  <span class="px-2 py-1 text-xs rounded-full"
                        [ngClass]="{
                          'bg-yellow-100 text-yellow-800': plan.paymentStatusDescription === 'Pendiente',
                          'bg-red-100 text-red-800': plan.isOverdue,
                          'bg-gray-100 text-gray-600': plan.isPunished
                        }">
                    {{ plan.isPunished ? 'Castigada' : (plan.isOverdue ? 'Vencida' : plan.paymentStatusDescription) }}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Resumen selección -->
      <div class="mt-6 bg-red-50 border border-red-200 rounded-lg p-4">
        <h4 class="font-medium text-red-800 mb-2">Resumen de Cuotas a Castigar</h4>
        <div class="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <p class="text-gray-600">Cuotas seleccionadas</p>
            <p class="font-semibold text-gray-900">{{ selectedInstallments.length }}</p>
          </div>
          <div>
            <p class="text-gray-600">Monto empresa</p>
            <p class="font-semibold text-gray-900">{{ formatCurrency(selectedCompanyAmount, selectedEnrollment.currencySymbol) }}</p>
          </div>
          <div>
            <p class="text-gray-600">Monto participantes</p>
            <p class="font-semibold text-gray-900">{{ formatCurrency(selectedParticipantAmount, selectedEnrollment.currencySymbol) }}</p>
          </div>
          <div>
            <p class="text-gray-600">Total a castigar</p>
            <p class="font-bold text-red-700 text-lg">{{ formatCurrency(selectedTotalAmount, selectedEnrollment.currencySymbol) }}</p>
          </div>
        </div>
      </div>

      <!-- Botones navegación -->
      <div class="flex justify-between mt-6">
        <button (click)="prevStep()" class="px-4 py-2 text-gray-600 hover:text-gray-800">
          Volver
        </button>
        <button (click)="nextStep()" 
                [disabled]="selectedInstallments.length === 0"
                class="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed">
          Continuar
        </button>
      </div>
    </div>

    <!-- Paso 3: Confirmar -->
    <div *ngIf="currentStep === 3 && selectedEnrollment" class="bg-white rounded-lg shadow-md p-6">
      <h2 class="text-lg font-semibold text-gray-900 mb-6">Confirmar Retiro</h2>
      
      <!-- Resumen del retiro -->
      <div class="bg-gray-50 rounded-lg p-4 mb-6">
        <h3 class="font-medium text-gray-700 mb-2">Resumen del Retiro</h3>
        <div class="grid grid-cols-2 gap-4 text-sm">
          <div>
            <span class="text-gray-500">Empresa:</span>
            <span class="ml-2 font-medium">{{ selectedEnrollment.companyName }}</span>
          </div>
          <div>
            <span class="text-gray-500">Proyecto:</span>
            <span class="ml-2 font-medium">{{ selectedEnrollment.projectMnemonic }}</span>
          </div>
          <div>
            <span class="text-gray-500">Cuotas a castigar:</span>
            <span class="ml-2 font-medium">{{ selectedInstallments.length }}</span>
          </div>
          <div>
            <span class="text-gray-500">Monto total:</span>
            <span class="ml-2 font-medium text-red-600">{{ formatCurrency(selectedTotalAmount, selectedEnrollment.currencySymbol) }}</span>
          </div>
        </div>
      </div>

      <form [formGroup]="confirmForm" (ngSubmit)="submitWithdrawal()">
        <div class="mb-4">
          <label class="block text-sm font-medium text-gray-700 mb-1">Razón del retiro *</label>
          <textarea 
            formControlName="reason"
            rows="3"
            placeholder="Ingrese la razón del retiro (mínimo 10 caracteres)"
            class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            [ngClass]="{'border-red-500': confirmForm.get('reason')?.invalid && confirmForm.get('reason')?.touched}">
          </textarea>
          <div *ngIf="confirmForm.get('reason')?.invalid && confirmForm.get('reason')?.touched" class="text-red-500 text-sm mt-1">
            La razón es requerida y debe tener al menos 10 caracteres
          </div>
        </div>

        <div class="mb-6">
          <label class="block text-sm font-medium text-gray-700 mb-1">Notas adicionales (opcional)</label>
          <textarea 
            formControlName="notes"
            rows="2"
            placeholder="Notas adicionales para auditoría"
            class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent">
          </textarea>
        </div>

        <!-- Advertencia -->
        <div class="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6">
          <div class="flex">
            <div class="flex-shrink-0">
              <svg class="h-5 w-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd"/>
              </svg>
            </div>
            <div class="ml-3">
              <p class="text-sm text-yellow-700">
                <strong>Importante:</strong> Esta acción castigará las cuotas seleccionadas y cambiará el estado del acuerdo a "Retirado". 
                Las cuotas castigadas no aparecerán en los reportes de cartera regular.
              </p>
            </div>
          </div>
        </div>

        <div class="flex justify-between">
          <button type="button" (click)="prevStep()" class="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50">
            Anterior
          </button>
          <div class="flex items-center gap-3">
            <button type="button" (click)="cancel()" class="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50">
              Cancelar
            </button>
            <button 
              type="submit" 
              [disabled]="confirmForm.invalid || isSubmitting"
              class="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:bg-gray-400 disabled:cursor-not-allowed inline-flex items-center gap-2">
              <svg *ngIf="isSubmitting" class="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <span *ngIf="isSubmitting">Procesando...</span>
              <span *ngIf="!isSubmitting">Confirmar Retiro</span>
            </button>
          </div>
        </div>
      </form>
    </div>

    <!-- Paso 4: Resultado -->
    <div *ngIf="currentStep === 4" class="bg-white rounded-lg shadow-md p-6">
      <!-- Éxito -->
      <div *ngIf="operationSuccess" class="text-center py-8">
        <div class="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
          <svg class="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
          </svg>
        </div>
        <h2 class="text-xl font-semibold text-gray-900 mb-2">Retiro Procesado Exitosamente</h2>
        <p class="text-gray-600 mb-6">{{ operationResult?.message }}</p>
        
        <div *ngIf="operationResult" class="bg-gray-50 rounded-lg p-4 text-left max-w-md mx-auto mb-6">
          <div class="grid grid-cols-2 gap-3 text-sm">
            <div>
              <p class="text-gray-500">Cuotas castigadas</p>
              <p class="font-semibold">{{ operationResult.affectedInstallmentsCount }}</p>
            </div>
            <div>
              <p class="text-gray-500">Monto total</p>
              <p class="font-semibold text-red-600">{{ formatCurrency(operationResult.totalAffectedAmount) }}</p>
            </div>
            <div>
              <p class="text-gray-500">Cuotas empresa</p>
              <p class="font-semibold">{{ operationResult.companyInstallmentsAffected }}</p>
            </div>
            <div>
              <p class="text-gray-500">Cuotas participantes</p>
              <p class="font-semibold">{{ operationResult.participantInstallmentsAffected }}</p>
            </div>
          </div>
        </div>

        <div class="flex justify-center gap-3">
          <button (click)="goToEnrollmentsList()" class="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50">
            Volver a inscripciones
          </button>
          <button (click)="startNewWithdrawal()" class="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
            Nuevo retiro
          </button>
        </div>
      </div>

      <!-- Error -->
      <div *ngIf="!operationSuccess" class="text-center py-8">
        <div class="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mb-4">
          <svg class="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
          </svg>
        </div>
        <h2 class="text-xl font-semibold text-gray-900 mb-2">Error al Procesar Retiro</h2>
        <p class="text-gray-600 mb-6">{{ operationError }}</p>
        
        <div class="flex justify-center gap-3">
          <button (click)="goToEnrollmentsList()" class="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50">
            Volver a inscripciones
          </button>
          <button (click)="prevStep()" class="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
            Intentar de nuevo
          </button>
        </div>
      </div>
    </div>
</div>

